import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Download, CheckCircle, Clock, AlertCircle, X, Trash2, RefreshCw, Zap } from 'lucide-react';
import { useAppContext, Invoice, InvoiceItem } from '../../context/AppContext';
const renewalClients = [{
  name: 'Sarah Jenkins',
  email: 'sarah@example.com',
  type: 'customer' as const,
  service: 'Web Hosting Pro',
  amount: 14.99,
  billing: 'monthly'
}, {
  name: 'Acme Corp',
  email: 'billing@acme.com',
  type: 'business' as const,
  service: 'Dedicated Server x2',
  amount: 199.98,
  billing: 'monthly'
}, {
  name: 'David Chen',
  email: 'david@example.com',
  type: 'customer' as const,
  service: 'Domain Renewal - davidchen.dev',
  amount: 12.99,
  billing: 'yearly'
}, {
  name: 'DJ Mike',
  email: 'mike@radio.com',
  type: 'customer' as const,
  service: 'Shoutcast Pro',
  amount: 14.99,
  billing: 'monthly'
}, {
  name: 'StreamNet LLC',
  email: 'accounts@streamnet.io',
  type: 'business' as const,
  service: 'Shoutcast Reseller Pro',
  amount: 59.99,
  billing: 'monthly'
}, {
  name: 'Emily Rodriguez',
  email: 'emily@design.co',
  type: 'customer' as const,
  service: 'SSL Certificate',
  amount: 4.99,
  billing: 'yearly'
}];
export function InvoiceManager() {
  const {
    invoices,
    addInvoice,
    updateInvoice
  } = useAppContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [lineItems, setLineItems] = useState<InvoiceItem[]>([{
    description: '',
    amount: 0
  }]);
  const [isGenerating, setIsGenerating] = useState(false);
  const filteredInvoices = invoices.filter(inv => inv.clientName.toLowerCase().includes(searchTerm.toLowerCase()) || inv.id.toLowerCase().includes(searchTerm.toLowerCase())).filter(inv => statusFilter === 'all' || inv.status === statusFilter);
  const handleMarkPaid = (id: string) => {
    updateInvoice(id, {
      status: 'paid',
      paidAt: new Date().toISOString()
    });
  };
  const handleCreateInvoice = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const validItems = lineItems.filter(item => item.description.trim() !== '' && item.amount > 0);
    if (validItems.length === 0) return;
    const total = validItems.reduce((sum, item) => sum + item.amount, 0);
    const now = new Date();
    const dueDate = new Date(form.get('dueDate') as string);
    const invoice: Invoice = {
      id: `INV-${now.getFullYear()}-${String(invoices.length + 1).padStart(3, '0')}`,
      clientName: form.get('clientName') as string,
      clientEmail: form.get('clientEmail') as string,
      clientType: form.get('clientType') as 'customer' | 'business',
      items: validItems,
      total,
      status: 'pending',
      createdAt: now.toISOString().split('T')[0],
      dueAt: dueDate.toISOString().split('T')[0]
    };
    addInvoice(invoice);
    setIsCreateOpen(false);
    setLineItems([{
      description: '',
      amount: 0
    }]);
  };
  const addLineItem = () => {
    setLineItems([...lineItems, {
      description: '',
      amount: 0
    }]);
  };
  const removeLineItem = (index: number) => {
    if (lineItems.length <= 1) return;
    setLineItems(lineItems.filter((_, i) => i !== index));
  };
  const updateLineItem = (index: number, field: keyof InvoiceItem, value: string | number) => {
    setLineItems(lineItems.map((item, i) => i === index ? {
      ...item,
      [field]: field === 'amount' ? parseFloat(value as string) || 0 : value
    } : item));
  };
  const lineItemsTotal = lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  const handleGenerateRenewals = () => {
    setIsGenerating(true);
    setTimeout(() => {
      const now = new Date();
      const dueDate = new Date(now);
      dueDate.setDate(dueDate.getDate() + 14);
      renewalClients.forEach((client, i) => {
        const existingIds = invoices.map(inv => inv.id);
        let newId: string;
        let counter = invoices.length + i + 1;
        do {
          newId = `INV-${now.getFullYear()}-${String(counter).padStart(3, '0')}`;
          counter++;
        } while (existingIds.includes(newId));
        const invoice: Invoice = {
          id: newId,
          clientName: client.name,
          clientEmail: client.email,
          clientType: client.type,
          items: [{
            description: `${client.service} — ${client.billing} renewal`,
            amount: client.amount
          }],
          total: client.amount,
          status: 'pending',
          createdAt: now.toISOString().split('T')[0],
          dueAt: dueDate.toISOString().split('T')[0]
        };
        addInvoice(invoice);
      });
      setIsGenerating(false);
    }, 1200);
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle size={16} className="text-emerald-500" data-id="element-359" />;
      case 'pending':
        return <Clock size={16} className="text-amber-500" data-id="element-360" />;
      case 'overdue':
        return <AlertCircle size={16} className="text-red-500" data-id="element-361" />;
      default:
        return null;
    }
  };
  const getStatusClass = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      case 'pending':
        return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
      case 'overdue':
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      default:
        return 'bg-slate-800 text-slate-400 border-slate-700';
    }
  };
  const defaultDueDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().split('T')[0];
  };
  return <div className="space-y-8" data-id="element-362">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-363">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} data-id="element-364">
          <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-365">
            Invoices
          </h1>
          <p className="text-slate-400" data-id="element-366">Manage billing and client payments.</p>
        </motion.div>
        <div className="flex gap-3" data-id="element-367">
          <button onClick={handleGenerateRenewals} disabled={isGenerating} className="bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors text-sm" data-id="element-368">
            {isGenerating ? <RefreshCw size={16} className="animate-spin" data-id="element-369" /> : <Zap size={16} data-id="element-370" />}
            {isGenerating ? 'Generating...' : 'Auto-Generate Renewals'}
          </button>
          <button onClick={() => setIsCreateOpen(true)} className="bg-fox-600 hover:bg-fox-500 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors" data-id="element-371">
            <Plus size={18} data-id="element-372" /> Create Invoice
          </button>
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex gap-2 flex-wrap" data-id="element-373">
        {['all', 'pending', 'paid', 'overdue', 'cancelled'].map(status => {
        const count = status === 'all' ? invoices.length : invoices.filter(i => i.status === status).length;
        return <button key={status} onClick={() => setStatusFilter(status)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${statusFilter === status ? 'bg-fox-600 text-white' : 'bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-white'}`} data-id="element-374">
              {status.charAt(0).toUpperCase() + status.slice(1)}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${statusFilter === status ? 'bg-white/20' : 'bg-slate-800'}`} data-id="element-375">
                {count}
              </span>
            </button>;
      })}
      </div>

      {/* Invoice Table */}
      <div className="bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden" data-id="element-376">
        <div className="p-4 border-b border-slate-800 flex items-center gap-4" data-id="element-377">
          <div className="relative flex-1 max-w-md" data-id="element-378">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} data-id="element-379" />
            <input type="text" placeholder="Search by client or invoice ID..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-10 pr-4 py-2 text-white focus:border-fox-500 focus:outline-none" data-id="element-380" />
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-381">
          <table className="w-full text-left border-collapse" data-id="element-382">
            <thead data-id="element-383">
              <tr className="border-b border-slate-800 text-slate-400 text-sm bg-slate-900/50" data-id="element-384">
                <th className="p-4 font-medium" data-id="element-385">Invoice #</th>
                <th className="p-4 font-medium" data-id="element-386">Client</th>
                <th className="p-4 font-medium" data-id="element-387">Items</th>
                <th className="p-4 font-medium" data-id="element-388">Amount</th>
                <th className="p-4 font-medium" data-id="element-389">Status</th>
                <th className="p-4 font-medium" data-id="element-390">Due Date</th>
                <th className="p-4 font-medium text-right" data-id="element-391">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800" data-id="element-392">
              {filteredInvoices.map(inv => <tr key={inv.id} className="hover:bg-slate-900/50 transition-colors" data-id="element-393">
                  <td className="p-4 font-medium text-white font-mono text-sm" data-id="element-394">
                    {inv.id}
                  </td>
                  <td className="p-4" data-id="element-395">
                    <p className="text-white font-medium" data-id="element-396">{inv.clientName}</p>
                    <p className="text-xs text-slate-500" data-id="element-397">{inv.clientEmail}</p>
                  </td>
                  <td className="p-4" data-id="element-398">
                    <div className="text-sm text-slate-400 max-w-[200px]" data-id="element-399">
                      {inv.items.map((item, i) => <p key={i} className="truncate" data-id="element-400">
                          {item.description}
                        </p>)}
                    </div>
                  </td>
                  <td className="p-4 text-white font-medium" data-id="element-401">
                    ${inv.total.toFixed(2)}
                  </td>
                  <td className="p-4" data-id="element-402">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusClass(inv.status)}`} data-id="element-403">
                      {getStatusIcon(inv.status)}
                      {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                    </span>
                  </td>
                  <td className="p-4 text-slate-400 text-sm" data-id="element-404">{inv.dueAt}</td>
                  <td className="p-4 text-right" data-id="element-405">
                    <div className="flex items-center justify-end gap-2" data-id="element-406">
                      {inv.status !== 'paid' && <button onClick={() => handleMarkPaid(inv.id)} className="px-3 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 text-xs font-medium rounded transition-colors" data-id="element-407">
                          Mark Paid
                        </button>}
                      <button className="p-1.5 text-slate-400 hover:text-white transition-colors" data-id="element-408">
                        <Download size={18} data-id="element-409" />
                      </button>
                    </div>
                  </td>
                </tr>)}
              {filteredInvoices.length === 0 && <tr data-id="element-410">
                  <td colSpan={7} className="p-8 text-center text-slate-500" data-id="element-411">
                    No invoices found.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Invoice Modal */}
      <AnimatePresence data-id="element-412">
        {isCreateOpen && <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-413">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-id="element-414">
              <div className="p-6 border-b border-slate-800 flex justify-between items-center sticky top-0 bg-slate-900 z-10" data-id="element-415">
                <h2 className="text-xl font-bold text-white" data-id="element-416">
                  Create New Invoice
                </h2>
                <button onClick={() => setIsCreateOpen(false)} className="text-slate-400 hover:text-white" data-id="element-417">
                  <X size={24} data-id="element-418" />
                </button>
              </div>

              <form onSubmit={handleCreateInvoice} className="p-6 space-y-6" data-id="element-419">
                {/* Client Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-420">
                  <div className="space-y-2" data-id="element-421">
                    <label className="text-sm font-medium text-slate-300" data-id="element-422">
                      Client Name *
                    </label>
                    <input name="clientName" required className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" placeholder="John Doe" data-id="element-423" />
                  </div>
                  <div className="space-y-2" data-id="element-424">
                    <label className="text-sm font-medium text-slate-300" data-id="element-425">
                      Client Email *
                    </label>
                    <input name="clientEmail" type="email" required className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" placeholder="john@example.com" data-id="element-426" />
                  </div>
                  <div className="space-y-2" data-id="element-427">
                    <label className="text-sm font-medium text-slate-300" data-id="element-428">
                      Client Type
                    </label>
                    <select name="clientType" className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-429">
                      <option value="customer" data-id="element-430">Customer</option>
                      <option value="business" data-id="element-431">Business</option>
                    </select>
                  </div>
                  <div className="space-y-2" data-id="element-432">
                    <label className="text-sm font-medium text-slate-300" data-id="element-433">
                      Due Date *
                    </label>
                    <input name="dueDate" type="date" required defaultValue={defaultDueDate()} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-434" />
                  </div>
                </div>

                {/* Line Items */}
                <div className="space-y-4" data-id="element-435">
                  <div className="flex items-center justify-between" data-id="element-436">
                    <label className="text-sm font-medium text-slate-300" data-id="element-437">
                      Line Items
                    </label>
                    <button type="button" onClick={addLineItem} className="text-xs text-fox-500 hover:text-fox-400 font-medium flex items-center gap-1" data-id="element-438">
                      <Plus size={14} data-id="element-439" /> Add Item
                    </button>
                  </div>

                  <div className="space-y-3" data-id="element-440">
                    {lineItems.map((item, index) => <div key={index} className="flex gap-3 items-start" data-id="element-441">
                        <div className="flex-1" data-id="element-442">
                          <input value={item.description} onChange={e => updateLineItem(index, 'description', e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none text-sm" placeholder="Service description" data-id="element-443" />
                        </div>
                        <div className="w-32" data-id="element-444">
                          <div className="relative" data-id="element-445">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm" data-id="element-446">
                              $
                            </span>
                            <input type="number" step="0.01" min="0" value={item.amount || ''} onChange={e => updateLineItem(index, 'amount', e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-7 pr-3 py-2.5 text-white focus:border-fox-500 focus:outline-none text-sm" placeholder="0.00" data-id="element-447" />
                          </div>
                        </div>
                        <button type="button" onClick={() => removeLineItem(index)} disabled={lineItems.length <= 1} className="p-2.5 text-slate-500 hover:text-red-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-448">
                          <Trash2 size={16} data-id="element-449" />
                        </button>
                      </div>)}
                  </div>

                  {/* Total */}
                  <div className="flex justify-end pt-4 border-t border-slate-800" data-id="element-450">
                    <div className="text-right" data-id="element-451">
                      <span className="text-slate-400 text-sm mr-4" data-id="element-452">
                        Total:
                      </span>
                      <span className="text-2xl font-bold text-white" data-id="element-453">
                        ${lineItemsTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-4 border-t border-slate-800 flex justify-end gap-3" data-id="element-454">
                  <button type="button" onClick={() => setIsCreateOpen(false)} className="px-4 py-2 text-slate-400 hover:text-white transition-colors" data-id="element-455">
                    Cancel
                  </button>
                  <button type="submit" disabled={lineItemsTotal <= 0} className="bg-fox-600 hover:bg-fox-500 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg font-medium transition-colors" data-id="element-456">
                    Create Invoice
                  </button>
                </div>
              </form>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}